import * as React from 'react'

import { BoxIcon } from 'lucide-react'
import { makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { NotificationType, SitePermitStatus } from '~/client/graph'
import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import DeliveryStatusIcon from '~/client/src/shared/components/DeliveryIconByStatus'
import DeliveryStatusLabel from '~/client/src/shared/components/DeliveryStatusLabel/DeliveryStatusLabel'
import { ImportantAtIcon } from '~/client/src/shared/components/icons/ImportantAtIcon'
import { ImportantBicIcon } from '~/client/src/shared/components/icons/ImportantBicIcon'
import { UserIcon } from '~/client/src/shared/components/icons/UserIcon'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import {
  getAttachmentFileIcon,
  getAttachmentFileType,
} from '~/client/src/shared/components/WorkflowCard/fileAttachmentHelpers'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import FileType from '~/client/src/shared/enums/FileType'
import { NotificationsListStore } from '~/client/src/shared/lists/notifications/NotificationsList.store'
import type { ListActivationEvent } from '~/client/src/shared/lists/useListInteraction'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import BaseNotification from '~/client/src/shared/models/Notification'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import DeliveriesStore from '~/client/src/shared/stores/domain/Deliveries.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import MessagesStore from '~/client/src/shared/stores/domain/MessagesStore/Messages.store'
import OffloadingEquipmentsStore from '~/client/src/shared/stores/domain/OffloadingEquipments.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import ZonesStore from '~/client/src/shared/stores/domain/Zones.store'
import { SAVE_WORKFLOWS } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import PermitBallInCourtStore from '~/client/src/shared/stores/ui/PermitBallInCourt.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { isDeleteType } from '~/client/src/shared/types/NotificationTypes'
import canApproveUser from '~/client/src/shared/utils/canApproveUser'
import {
  EntityData,
  getAuthorAndAction,
  getCodeAndTitle,
  getDateDisplay,
  getEntityTypeName,
  getImportantKind,
  getUserSignupMeta,
  resolveEntityData,
} from '~/client/src/shared/utils/notificationItemHelpers'
import {
  currentStepHasMandatoryFields,
  getForwardActionLabel,
  getForwardActionTargetStatus,
} from '~/client/src/shared/utils/permitStepActionHelpers'
import { INTERPUNCT } from '~/client/src/shared/utils/usefulStrings'
import { advanceToNextWorkflowStep } from '~/client/src/shared/utils/workflowStepUtils'

import DesktopInitialState from '../../../stores/DesktopInitialState'
import DesktopCommonStore from '../../../stores/ui/DesktopCommon.store'
import NotificationPhotos from './NotificationPhotos'

import './Notifications.scss'

// translated

interface IProps {
  recordId: string
  notification: BaseNotification
  deliveriesStore?: DeliveriesStore
  messagesStore?: MessagesStore
  companiesStore?: CompaniesStore
  workflowTypesStore?: WorkflowTypesStore
  projectMembersStore?: ProjectMembersStore
  projectDateStore?: ProjectDateStore
  zonesStore?: ZonesStore
  offloadingEquipmentsStore?: OffloadingEquipmentsStore
  locationAttributesStore?: LocationAttributesStore
  tagsStore?: TagsStore
  materialsStore?: MaterialsStore
  userProjectsStore?: UserProjectsStore
  permitBicStore?: PermitBallInCourtStore
  eventsStore?: EventsStore
  notificationsListStore: NotificationsListStore
  showImportantColumn?: boolean
  interactionHandlers: {
    onClick(e: React.MouseEvent): void
    onSelectionMouseDown(e: React.MouseEvent): void
    onPointerDown(e: React.PointerEvent): void
    onPointerMove(e: React.PointerEvent): void
    onPointerUp(e: React.PointerEvent): void
    onPointerCancel(e: React.PointerEvent): void
  }
  toggleNotification: (id: string, event?: React.MouseEvent) => void
  activateNotification: (id: string, event: ListActivationEvent) => void
  common?: DesktopCommonStore
  state?: DesktopInitialState
}

@inject(
  'deliveriesStore',
  'messagesStore',
  'companiesStore',
  'workflowTypesStore',
  'projectMembersStore',
  'projectDateStore',
  'zonesStore',
  'offloadingEquipmentsStore',
  'locationAttributesStore',
  'tagsStore',
  'materialsStore',
  'userProjectsStore',
  'permitBicStore',
  'eventsStore',
  'state',
)
@observer
export default class NotificationsListItem extends React.Component<IProps> {
  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const {
      notification: { id, wasRead },
    } = this.props
    const isSelected = this.isSelected

    const entityData = this.entityData

    const listItemClasses = classList({
      'notifications-list-item-read': wasRead,
      'notifications-list-item-unread': !wasRead,
      'notifications-list-item-active': isSelected,
    })

    return (
      <div
        key={id}
        className={listItemClasses}
        role="button"
        tabIndex={0}
        onClick={this.props.interactionHandlers.onClick}
        onPointerDown={this.props.interactionHandlers.onPointerDown}
        onPointerMove={this.props.interactionHandlers.onPointerMove}
        onPointerUp={this.props.interactionHandlers.onPointerUp}
        onPointerCancel={this.props.interactionHandlers.onPointerCancel}
        onKeyDown={e => {
          if (e.target !== e.currentTarget) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            this.props.activateNotification(id, e)
          }
        }}
      >
        <div className="row y-start mr6 gap-8">
          {/* Left gutter: checkbox + important icon + entity icon */}
          <div
            className="row y-start no-grow notification-card-gutter gap-6 tw:cursor-pointer tw:self-stretch"
            onMouseDown={this.props.interactionHandlers.onSelectionMouseDown}
            onClick={this.selectNotification}
          >
            <span className="no-grow no-shrink pa4 tw:flex tw:justify-center">
              <Checkbox checked={isSelected} />
            </span>
            <span className="row x-center y-center no-grow no-shrink w24 h24">
              {this.renderImportantIcon()}
            </span>
            <span className="row x-center y-center no-grow no-shrink">
              {this.renderEntityIcon(entityData)}
            </span>
          </div>

          {/* Content column */}
          <div className="col full-width overflow-hidden">
            {/* Row 1: title + type + date + status + actions + timestamp */}
            <div className="row y-center tw:min-h-6">
              <span
                className={`text large no-white-space-wrap no-grow ${
                  wasRead
                    ? 'notification-secondary-text'
                    : 'bold notification-dark-text'
                }`}
              >
                {getCodeAndTitle(
                  entityData,
                  this.props.notification,
                  this.props.companiesStore,
                )}
              </span>
              {this.renderTypeName(entityData)}
              {this.renderDateDisplay(entityData)}
              <span className="no-grow notification-card-status">
                {this.renderStatusBadge(entityData)}
              </span>
              {this.renderBicActionButton()}
              {this.renderApproveActionButton()}
              <span
                className={`text no-grow no-white-space-wrap h24 row y-center ${
                  this.bicActionButtonText || this.approveActionButtonText
                    ? 'ml6'
                    : 'ml-auto'
                } ${
                  wasRead
                    ? 'notification-secondary-text'
                    : 'bold notification-dark-text'
                }`}
              >
                {this.formattedTimestamp}
              </span>
            </div>

            {/* Row 2: All objects — company, resp, locations, equipment, materials */}
            {this.renderObjectsRow()}

            {/* Row 3: Notification message (author + action) */}
            <div className="row y-center tw:mt-2">
              <span className="text large ellipsis notification-secondary-text">
                {getAuthorAndAction(
                  this.props.notification,
                  entityData,
                  this.props.projectMembersStore,
                  this.props.messagesStore,
                )}
              </span>
            </div>

            {/* Photos (conditional) */}
            <div className="row pt4">
              <NotificationPhotos notification={this.props.notification} />
            </div>

            {/* Attachments (conditional) */}
            {this.renderAttachmentsRow(entityData)}
          </div>
        </div>
      </div>
    )
  }

  private get bicActionButtonText(): string | null {
    const { notification, workflowTypesStore, state, permitBicStore } =
      this.props
    const userId = state?.user?.id
    const kind = getImportantKind(
      notification,
      userId,
      workflowTypesStore,
      permitBicStore,
    )
    const isAdmin = state?.userActiveProjectSettings?.isAdmin
    if (kind !== 'bic' && !isAdmin) return null

    const data = this.entityData
    if (data.kind !== 'permit' || !data.permit) return null

    const permit = data.permit
    if (permit.isDoneOrDenied || permit.isDeleted) return null

    const permitType = workflowTypesStore?.getPermitTypeById(permit.typeId)
    if (!permitType) return null

    const step = permitType.workflowSteps.find(
      s => s.id === permit.currentWorkflowStepId,
    )
    if (!step) return null

    const actionLabel = getForwardActionLabel(step.type)
    if (!actionLabel) return null

    const hasMandatory = currentStepHasMandatoryFields(
      permit,
      workflowTypesStore,
    )
    return hasMandatory ? Localization.translator.goToStep : actionLabel
  }

  private get formattedTimestamp(): string {
    return this.props.projectDateStore.getSmartTimestamp(
      this.props.notification.createdAt,
    )
  }

  private get entityData(): EntityData {
    return resolveEntityData(this.props.notification, {
      deliveriesStore: this.props.deliveriesStore,
    })
  }

  private renderTypeName(entityData: EntityData): JSX.Element {
    const typeName = getEntityTypeName(
      entityData,
      this.props.workflowTypesStore,
    )
    if (!typeName) return null

    return (
      <span className="text large no-grow no-white-space-wrap notification-secondary-text">
        {` ${INTERPUNCT} ${typeName}`}
      </span>
    )
  }

  private renderDateDisplay(entityData: EntityData): JSX.Element {
    const dateDisplay = getDateDisplay(entityData, this.props.projectDateStore)
    if (!dateDisplay) return null

    return (
      <span className="text large no-grow no-white-space-wrap notification-secondary-text">
        {` ${INTERPUNCT} ${dateDisplay}`}
      </span>
    )
  }

  private get isSelected(): boolean {
    return this.props.notificationsListStore.list.selection.selectedIds.has(
      this.props.recordId,
    )
  }

  private renderEntityIcon(data: EntityData): JSX.Element {
    const { notification, workflowTypesStore } = this.props
    const { type } = notification

    if (isDeleteType(type)) return null

    if (type === NotificationType.NewUserCreatedViaSignUp) {
      return <UserIcon size="md" className="tw:text-foreground" />
    }

    switch (data.kind) {
      case 'permit':
        if (!data.permit) return null
        return (
          <WorkflowTypeIcon
            iconName={data.permit.getIconNameOfPermitType(workflowTypesStore)}
          />
        )
      case 'delivery':
        if (!data.delivery) return null
        return <DeliveryStatusIcon status={data.delivery.status} />
      default:
        return null
    }
  }

  private renderImportantIcon(): JSX.Element {
    const { notification, workflowTypesStore, state, permitBicStore } =
      this.props
    const userId = state?.user?.id
    const kind = getImportantKind(
      notification,
      userId,
      workflowTypesStore,
      permitBicStore,
    )

    switch (kind) {
      case 'bic':
        return (
          <span className="row x-center y-center no-grow no-shrink w20 h20">
            <ImportantBicIcon size="sm" />
          </span>
        )
      case 'mention':
        return (
          <span className="row x-center y-center no-grow no-shrink w20 h20">
            <ImportantAtIcon size="sm" />
          </span>
        )
      default:
        return <span className="row no-grow no-shrink w20 h20" />
    }
  }

  private renderObjectsRow(): JSX.Element {
    const entityData = this.entityData
    const items: Array<{ key: string; node: JSX.Element }> = []

    if (entityData.kind === 'permit' && entityData.permit) {
      const { companyIds, responsibleIds, locations, equipment, materials } =
        entityData.permit
      const { getCompanyById } = this.props.companiesStore
      const { getByIds } = this.props.projectMembersStore
      const { getById: getLocationById } = this.props.locationAttributesStore
      const { getByIds: getMaterialsByIds } = this.props.materialsStore

      // Companies
      const companies = companyIds
        ?.map(id => getCompanyById(id))
        .filter(Boolean)
      companies?.forEach(company => {
        items.push({
          key: `co-${company.id}`,
          node: (
            <>
              <Avatar
                src={company.avatarUrl}
                size={AvatarSize.XMinuscule}
                className="mr4"
              />
              <span className="text notification-secondary-text no-grow no-white-space-wrap">
                {company.name}
              </span>
            </>
          ),
        })
      })

      // Responsibles
      const responsibles = getByIds(responsibleIds)?.filter(Boolean)
      responsibles?.forEach(responsible => {
        items.push({
          key: `resp-${responsible.id}`,
          node: (
            <>
              <Avatar
                src={responsible.avatarUrl}
                size={AvatarSize.XMinuscule}
                className="mr4"
              />
              <span className="text notification-secondary-text no-grow no-white-space-wrap">
                {responsible.fullName}
              </span>
            </>
          ),
        })
      })

      const { tagStoreByTagTypeMap } = this.props.tagsStore

      // Locations
      locations
        .map(l => getLocationById(l.id))
        .filter(Boolean)
        .forEach(dto => {
          const hierarchy = dto.getHierarchyChainObjs(tagStoreByTagTypeMap)
          const hierarchyNames = hierarchy.length
            ? ` ${INTERPUNCT} ${hierarchy
                .map(h => h.name)
                .join(` ${INTERPUNCT} `)}`
            : ''
          const title = `${dto.name}${hierarchyNames}`

          items.push({
            key: `loc-${dto.id}`,
            node: (
              <span
                title={title}
                className="no-grow no-white-space-wrap"
                style={{ color: dto.color }}
              >
                <SitemapAttributeTag
                  dataObject={dto}
                  shouldShowAsTag={false}
                  iconSize={16}
                >
                  <span className="text notification-secondary-text">
                    {dto.name}
                  </span>
                </SitemapAttributeTag>
              </span>
            ),
          })
        })

      // Equipment
      equipment
        .map(e => getLocationById(e.id))
        .filter(Boolean)
        .forEach(dto => {
          const hierarchy = dto.getHierarchyChainObjs(tagStoreByTagTypeMap)
          const hierarchyNames = hierarchy.length
            ? ` ${INTERPUNCT} ${hierarchy
                .map(h => h.name)
                .join(` ${INTERPUNCT} `)}`
            : ''
          const title = `${dto.name}${hierarchyNames}`

          items.push({
            key: `eq-${dto.id}`,
            node: (
              <span
                title={title}
                className="no-grow no-white-space-wrap"
                style={{ color: dto.color }}
              >
                <SitemapAttributeTag
                  dataObject={dto}
                  shouldShowAsTag={false}
                  iconSize={16}
                >
                  <span className="text notification-secondary-text">
                    {dto.name}
                  </span>
                </SitemapAttributeTag>
              </span>
            ),
          })
        })

      // Materials
      getMaterialsByIds(materials.map(m => m.materialId)).forEach(material => {
        items.push({
          key: `mat-${material.id}`,
          node: (
            <span className="no-grow no-white-space-wrap text notification-secondary-text tw:pl-1 tw:inline-flex tw:items-center">
              <BoxIcon className="tw:w-4 tw:h-4 tw:mr-1 tw:shrink-0" />
              {material.productName}
            </span>
          ),
        })
      })
    } else if (entityData.kind === 'delivery' && entityData.delivery) {
      // Company
      const companyName = this.props.companiesStore.getCompanyNameById(
        entityData.delivery.company,
      )
      if (companyName) {
        items.push({
          key: 'del-company',
          node: (
            <span className="text notification-secondary-text no-grow no-white-space-wrap">
              {companyName}
            </span>
          ),
        })
      }

      // Zone
      const zoneName = this.props.zonesStore.byId.get(
        entityData.delivery.zone,
      )?.name
      if (zoneName) {
        items.push({
          key: 'del-zone',
          node: (
            <span className="text notification-secondary-text no-grow no-white-space-wrap">
              {zoneName}
            </span>
          ),
        })
      }

      // Equipment
      const equipText = this.props.offloadingEquipmentsStore.getString(
        entityData.delivery.offloadingEquipments,
        entityData.delivery.isDone,
      )
      if (equipText && equipText !== '—') {
        items.push({
          key: 'del-equip',
          node: (
            <span className="text notification-secondary-text no-grow no-white-space-wrap">
              {equipText}
            </span>
          ),
        })
      }
    }

    // User access request: show company · user name (no avatar)
    if (
      this.props.notification.type === NotificationType.NewUserCreatedViaSignUp
    ) {
      const { companyId, companyName, userName } = getUserSignupMeta(
        this.props.notification,
        this.props.projectMembersStore,
        this.props.companiesStore,
        this.props.userProjectsStore,
      )
      if (companyName) {
        const company = companyId
          ? this.props.companiesStore.getCompanyById(companyId)
          : null
        items.push({
          key: 'signup-company',
          node: (
            <>
              {company && (
                <Avatar
                  src={company.avatarUrl}
                  size={AvatarSize.XMinuscule}
                  className="mr4"
                />
              )}
              <span className="text notification-secondary-text no-grow no-white-space-wrap">
                {companyName}
              </span>
            </>
          ),
        })
      }
      if (userName) {
        items.push({
          key: 'signup-user',
          node: (
            <span className="text notification-secondary-text no-grow no-white-space-wrap">
              {userName}
            </span>
          ),
        })
      }
    }

    if (!items.length) return null

    return (
      <div className="row y-center tw:flex-wrap tw:mt-0.5">
        {items.map(({ key, node }, index) => (
          <React.Fragment key={key}>
            {index > 0 && (
              <span className="no-grow px4 notification-secondary-text">
                {INTERPUNCT}
              </span>
            )}
            {node}
          </React.Fragment>
        ))}
      </div>
    )
  }

  private renderStatusBadge(data: EntityData): JSX.Element {
    const { notification } = this.props

    if (notification.type === NotificationType.NewUserCreatedViaSignUp) {
      const user = this.props.projectMembersStore.getById(notification.entityId)
      const canApprove =
        user && canApproveUser(user, this.props.userProjectsStore)
      return (
        <StatusBadge
          status={
            canApprove ? SitePermitStatus.Requested : SitePermitStatus.Accepted
          }
          isLate={false}
          showDot={false}
        />
      )
    }

    switch (data.kind) {
      case 'delivery':
        if (!data.delivery) return null
        return <DeliveryStatusLabel status={data.delivery.status} />
      case 'permit':
        if (!data.permit) return null
        return (
          <StatusBadge
            status={data.permit.status}
            isLate={data.permit.isLate}
            showDot={false}
          />
        )
      default:
        return null
    }
  }

  private get approveActionButtonText(): string | null {
    const { notification, projectMembersStore, userProjectsStore } = this.props
    if (notification.type !== NotificationType.NewUserCreatedViaSignUp)
      return null

    const user = projectMembersStore.getById(notification.entityId)
    if (!user) return null
    if (!canApproveUser(user, userProjectsStore)) return null

    return Localization.translator.approve_verb
  }

  private renderBicActionButton(): JSX.Element {
    const buttonText = this.bicActionButtonText
    if (!buttonText) return null

    return (
      <span className="no-grow no-shrink ml-auto">
        <Button variant="primary-outline" onClick={this.handleBicAction}>
          {buttonText}
        </Button>
      </span>
    )
  }

  private renderApproveActionButton(): JSX.Element {
    const buttonText = this.approveActionButtonText
    if (!buttonText) return null

    return (
      <span className="no-grow no-shrink ml-auto">
        <Button variant="primary-outline" onClick={this.handleApproveAction}>
          {buttonText}
        </Button>
      </span>
    )
  }

  private renderAttachmentsRow(entityData: EntityData): JSX.Element {
    if (entityData.kind !== 'permit' || !entityData.permit) return null

    const attachments = entityData.permit.attachments || []
    if (!attachments.length) return null

    const MAX_PREVIEWS = 3
    const itemsOverLimit = attachments.length - MAX_PREVIEWS

    return (
      <div className="tw:flex tw:items-start tw:gap-2 tw:pt-1">
        {attachments.slice(0, MAX_PREVIEWS).map(attachment => {
          const type = getAttachmentFileType(attachment.fileName) as FileType

          switch (type) {
            case FileType.Image:
              return (
                <div
                  key={attachment.url}
                  className="tw:rounded tw:overflow-hidden tw:bg-muted tw:shrink-0 tw:grow-0 tw:relative tw:w-[80px] tw:h-[55px]"
                >
                  <img
                    src={attachment.url}
                    alt={attachment.fileName}
                    className="tw:absolute tw:inset-0 tw:w-full tw:h-full tw:object-cover tw:rounded"
                  />
                </div>
              )
            case FileType.Video:
              return (
                <div
                  key={attachment.url}
                  className="tw:rounded tw:overflow-hidden tw:bg-muted tw:shrink-0 tw:grow-0 tw:relative tw:w-[80px] tw:h-[55px]"
                >
                  <video
                    src={attachment.url}
                    className="tw:absolute tw:inset-0 tw:w-full tw:h-full tw:object-cover"
                    muted
                    preload="metadata"
                  />
                </div>
              )
            default:
              return (
                <div
                  key={attachment.url}
                  className="tw:rounded tw:overflow-hidden tw:bg-muted tw:shrink-0 tw:flex tw:items-center tw:justify-center"
                  style={{ width: 80, height: 55 }}
                >
                  {getAttachmentFileIcon(attachment.fileName, 'tw:w-6 tw:h-6')}
                </div>
              )
          }
        })}
        {itemsOverLimit > 0 && (
          <div
            className="tw:rounded tw:flex tw:items-center tw:justify-center tw:bg-muted tw:shrink-0"
            style={{ width: 80, height: 55 }}
          >
            <span className="text large bold">+{itemsOverLimit}</span>
          </div>
        )}
      </div>
    )
  }

  private handleApproveAction = async (e: React.SyntheticEvent) => {
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent.stopImmediatePropagation()

    const { notification, projectMembersStore } = this.props
    const user = projectMembersStore.getById(notification.entityId)
    if (!user) return

    await projectMembersStore.approveProjectMembers([user])
  }

  private handleBicAction = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent.stopImmediatePropagation()

    const data = this.entityData
    if (data.kind !== 'permit' || !data.permit) return

    const permit = data.permit
    const { workflowTypesStore, eventsStore } = this.props
    const hasMandatory = currentStepHasMandatoryFields(
      permit,
      workflowTypesStore,
    )

    if (hasMandatory) {
      this.props.activateNotification(this.props.notification.id, e)
      return
    }

    const permitType = workflowTypesStore?.getPermitTypeById(permit.typeId)
    if (!permitType) return

    const step = permitType.workflowSteps.find(
      s => s.id === permit.currentWorkflowStepId,
    )
    if (!step) return

    const isLastStep = permitType.isLastStep(permit.currentWorkflowStepId)
    const targetStatus = getForwardActionTargetStatus(step.type, isLastStep)
    if (!targetStatus) return

    permit.setStatus(targetStatus)
    advanceToNextWorkflowStep(permit, permitType.stepsWithoutRecurring)
    eventsStore.dispatch(SAVE_WORKFLOWS, [permit])
  }

  private selectNotification = (e: React.MouseEvent<HTMLDivElement>) => {
    const {
      notification: { id },
      toggleNotification,
    } = this.props
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent.stopImmediatePropagation()
    toggleNotification(id, e)
  }
}
